import React, { useState, useCallback, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  HiOutlineDocumentText, HiOutlinePlus, HiOutlineX, HiOutlineGlobe, HiOutlineCog,
} from 'react-icons/hi';
import LogViewer from '../components/LogViewer';
import {
  getCloudRunLogs, getCloudRunJobLogs, getCloudRunServicesList, getCloudRunJobs,
} from '../services/api';
import { TIME_PRESETS, getPresetRange } from '../shared/constants';
import selectStyles from '../shared/selectStyles';
import '../pages/ProductView.css';
import './CloudLogs.css';

const RESOURCE_TYPES = [
  { value: 'service', label: 'Cloud Run Service', icon: HiOutlineGlobe },
  { value: 'job', label: 'Cloud Run Job', icon: HiOutlineCog },
];

function makeSourceId() {
  return `src-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function CloudLogs({ gcpProjects }) {
  const [logSources, setLogSources] = useState([]);
  const [activeSourceId, setActiveSourceId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logTimePreset, setLogTimePreset] = useState('1h');
  const [logStartTime, setLogStartTime] = useState('');
  const [logEndTime, setLogEndTime] = useState('');
  const [logSeverityFilter, setLogSeverityFilter] = useState([]);
  const [showLogTimePresets, setShowLogTimePresets] = useState(false);
  const [showLogSeverityDropdown, setShowLogSeverityDropdown] = useState(false);
  const [logsPageSize, setLogsPageSize] = useState(50);
  const [logsCurrentPage, setLogsCurrentPage] = useState(1);

  const activeSource = logSources.find(s => s.id === activeSourceId) || null;

  const fetchLogs = useCallback(async () => {
    if (!activeSource) return;
    setLogsLoading(true);
    setLogsCurrentPage(1);
    try {
      let startTime;
      let endTime;
      if (logTimePreset === 'custom') {
        startTime = logStartTime || undefined;
        endTime = logEndTime || undefined;
      } else {
        const preset = TIME_PRESETS.find(p => p.value === logTimePreset);
        if (preset) {
          const range = getPresetRange(preset.hours);
          startTime = range.startTime;
          endTime = range.endTime;
        }
      }

      const options = {
        startTime,
        endTime,
        severity: logSeverityFilter,
        maxLogs: 1000,
      };

      let res;
      if (activeSource.resourceType === 'service') {
        res = await getCloudRunLogs(activeSource.projectId, activeSource.name, activeSource.region, options);
      } else {
        res = await getCloudRunJobLogs(activeSource.projectId, activeSource.name, activeSource.region, {
          ...options,
          executionName: activeSource.executionName || undefined,
        });
      }
      setLogs(res.data?.logs || []);
    } catch (err) {
      toast.error('Failed to fetch logs: ' + (err.response?.data?.error || err.message));
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [activeSource, logTimePreset, logStartTime, logEndTime, logSeverityFilter]);

  useEffect(() => {
    setLogs([]);
    if (activeSource) fetchLogs();
  }, [activeSourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeSource && logTimePreset !== 'custom') fetchLogs();
  }, [logTimePreset, logSeverityFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeSource = (id) => {
    setLogSources(prev => prev.filter(s => s.id !== id));
    if (activeSourceId === id) {
      const remaining = logSources.filter(s => s.id !== id);
      setActiveSourceId(remaining[0]?.id || null);
      setLogs([]);
    }
  };

  const addSource = (source) => {
    setLogSources(prev => [...prev, source]);
    setActiveSourceId(source.id);
    setShowAddModal(false);
  };

  const getResourceName = (log) => {
    if (activeSource?.resourceType === 'job') {
      return log.resource?.labels?.job_name || activeSource.name;
    }
    return log.resource?.labels?.service_name || activeSource?.name || '-';
  };

  const closeDropdowns = () => {
    setShowLogTimePresets(false);
    setShowLogSeverityDropdown(false);
  };

  return (
    <div className="cloud-logs page-container" onClick={closeDropdowns}>
      <div className="gcp-page-header">
        <div className="gcp-page-title-row">
          <h1><HiOutlineDocumentText size={28} /> Logs</h1>
          <span className="pv-subtitle">Cloud Run services &amp; jobs</span>
        </div>
        <div className="gcp-page-toolbar">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>
            <HiOutlinePlus size={14} /> Add Log Source
          </button>
        </div>
      </div>

      {logSources.length > 0 && (
        <div className="log-sources-bar">
          {logSources.map(src => (
            <button
              key={src.id}
              type="button"
              className={`log-source-chip ${activeSourceId === src.id ? 'active' : ''}`}
              onClick={() => setActiveSourceId(src.id)}
            >
              <span className="log-source-type">{src.resourceType === 'service' ? 'Service' : 'Job'}</span>
              <span className="log-source-name">{src.name}</span>
              <span className="log-source-meta">{src.projectName} · {src.region}</span>
              <span
                className="log-source-remove"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); removeSource(src.id); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); removeSource(src.id); } }}
              >
                <HiOutlineX size={14} />
              </span>
            </button>
          ))}
        </div>
      )}

      {!activeSource ? (
        <div className="empty-state cloud-logs-empty">
          <HiOutlineDocumentText size={48} />
          <h3>No log sources yet</h3>
          <p>Add a Cloud Run service or job to view its logs.</p>
          <button type="button" className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <HiOutlinePlus size={16} /> Add Log Source
          </button>
        </div>
      ) : (
        <LogViewer
          logs={logs}
          logsLoading={logsLoading}
          fetchLogs={fetchLogs}
          logTimePreset={logTimePreset}
          setLogTimePreset={setLogTimePreset}
          logStartTime={logStartTime}
          setLogStartTime={setLogStartTime}
          logEndTime={logEndTime}
          setLogEndTime={setLogEndTime}
          logSeverityFilter={logSeverityFilter}
          setLogSeverityFilter={setLogSeverityFilter}
          showLogTimePresets={showLogTimePresets}
          setShowLogTimePresets={setShowLogTimePresets}
          showLogSeverityDropdown={showLogSeverityDropdown}
          setShowLogSeverityDropdown={setShowLogSeverityDropdown}
          logsPageSize={logsPageSize}
          setLogsPageSize={setLogsPageSize}
          logsCurrentPage={logsCurrentPage}
          setLogsCurrentPage={setLogsCurrentPage}
          resourceColumnLabel={activeSource.resourceType === 'job' ? 'Job' : 'Service'}
          getResourceName={getResourceName}
        />
      )}

      {showAddModal && (
        <AddLogSourceModal
          gcpProjects={gcpProjects}
          onClose={() => setShowAddModal(false)}
          onAdd={addSource}
        />
      )}
    </div>
  );
}

function AddLogSourceModal({ gcpProjects, onClose, onAdd }) {
  const [projectId, setProjectId] = useState(gcpProjects[0]?._id || '');
  const [resourceType, setResourceType] = useState('service');
  const [resources, setResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [filterLatestExecution, setFilterLatestExecution] = useState(false);

  const projectOptions = gcpProjects.map(p => ({
    value: p._id,
    label: p.projectName || p.projectId,
  }));

  const loadResources = useCallback(async () => {
    if (!projectId) return;
    setLoadingResources(true);
    setSelectedResource(null);
    try {
      if (resourceType === 'service') {
        const res = await getCloudRunServicesList(projectId);
        setResources((res.data || []).map(s => ({
          value: `${s.name}|${s.region}`,
          label: `${s.name} (${s.region})`,
          name: s.name,
          region: s.region,
        })));
      } else {
        const res = await getCloudRunJobs(projectId);
        setResources((res.data || []).map(j => ({
          value: `${j.name}|${j.region}`,
          label: `${j.name} (${j.region})`,
          name: j.name,
          region: j.region,
          lastExecutionName: j.lastExecution?.name || null,
        })));
      }
    } catch (err) {
      toast.error('Failed to load resources: ' + (err.response?.data?.error || err.message));
      setResources([]);
    } finally {
      setLoadingResources(false);
    }
  }, [projectId, resourceType]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const handleAdd = () => {
    if (!projectId || !selectedResource) {
      toast.error('Select a project and resource');
      return;
    }
    const project = gcpProjects.find(p => p._id === projectId);
    onAdd({
      id: makeSourceId(),
      projectId,
      projectName: project?.projectName || project?.projectId || projectId,
      resourceType,
      name: selectedResource.name,
      region: selectedResource.region,
      executionName: resourceType === 'job' && filterLatestExecution ? selectedResource.lastExecutionName : null,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content cloud-logs-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Log Source</h2>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>GCP Project</label>
            <Select
              styles={selectStyles}
              options={projectOptions}
              value={projectOptions.find(o => o.value === projectId) || null}
              onChange={(opt) => setProjectId(opt?.value || '')}
              placeholder="Select project..."
              isSearchable
            />
          </div>

          <div className="form-group">
            <label>Resource Type</label>
            <div className="resource-type-toggle">
              {RESOURCE_TYPES.map(rt => {
                const Icon = rt.icon;
                return (
                  <button
                    key={rt.value}
                    type="button"
                    className={`resource-type-btn ${resourceType === rt.value ? 'active' : ''}`}
                    onClick={() => setResourceType(rt.value)}
                  >
                    <Icon size={16} /> {rt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="form-group">
            <label>{resourceType === 'service' ? 'Cloud Run Service' : 'Cloud Run Job'}</label>
            <Select
              styles={selectStyles}
              options={resources}
              value={selectedResource}
              onChange={setSelectedResource}
              placeholder={loadingResources ? 'Loading...' : 'Select resource...'}
              isLoading={loadingResources}
              isDisabled={!projectId || loadingResources}
              isSearchable
            />
          </div>

          {resourceType === 'job' && selectedResource?.lastExecutionName && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filterLatestExecution}
                  onChange={(e) => setFilterLatestExecution(e.target.checked)}
                />
                Filter to latest execution only ({selectedResource.lastExecutionName})
              </label>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleAdd} disabled={!selectedResource}>
            Add &amp; View Logs
          </button>
        </div>
      </div>
    </div>
  );
}

export default CloudLogs;
